import axios from 'axios';
import { getLocalItem, setLocalItem, getSessionItem, setSessionItem, removeLocalItem, removeSessionItem, isLocalStorageAvailable } from '@/utils/safeStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
export const API = `${BACKEND_URL}/api`;

// In-memory token fallback for iOS Private Mode where storage is completely blocked
let inMemoryToken = null;

/**
 * Get auth token with iOS-safe fallback
 * Priority: localStorage -> sessionStorage -> in-memory
 */
export const getAuthToken = () => {
  const localToken = getLocalItem('auth_token');
  if (localToken) return localToken;

  // Migrate legacy 'token' key to 'auth_token' (backward compat)
  const legacyToken = getLocalItem('token');
  if (legacyToken) {
    setLocalItem('auth_token', legacyToken);
    removeLocalItem('token');
    return legacyToken;
  }
  
  const sessionToken = getSessionItem('auth_token');
  if (sessionToken) return sessionToken;
  
  return inMemoryToken;
};

/**
 * Set auth token with iOS-safe fallback
 */
export const setAuthToken = (token, remember = true) => {
  // Always store in memory as ultimate fallback
  inMemoryToken = token;
  
  // Clear any existing tokens first
  removeLocalItem('auth_token');
  removeSessionItem('auth_token');
  
  if (token) {
    if (remember) {
      setLocalItem('auth_token', token);
    } else {
      setSessionItem('auth_token', token);
    }
  }
};

/**
 * Clear all auth tokens
 */
export const clearAuthToken = () => {
  inMemoryToken = null;
  removeLocalItem('auth_token');
  removeSessionItem('auth_token');
};

/**
 * Detect iOS Safari/WebView
 */
const isIOSSafari = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
  return isIOS && isSafari;
};

/**
 * Detect iOS WebView (in-app browser)
 */
const isIOSWebView = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  // WebView typically doesn't have Safari in UA or has specific markers
  const isWebView = isIOS && (!/Safari/.test(ua) || /FBAN|FBAV|Instagram|Twitter|LinkedIn/.test(ua));
  return isWebView;
};

// Create axios instance with iOS-optimized settings
const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30000, // 30 second timeout (iOS can be slow)
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

// Configure request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    // Use safe storage utilities for iOS compatibility
    const token = getAuthToken();
    
    if (token) {
      // Ensure headers object exists
      if (!config.headers) {
        config.headers = {};
      }
      config.headers.Authorization = `Bearer ${token}`;
      
      if (process.env.NODE_ENV === 'development' || config.url?.includes('/auth/')) {
        console.log('[Auth] Added token to request:', config.url);
      }
    }
    
    // iOS Safari diagnostics for auth requests
    if (config.url?.includes('/auth/')) {
      console.log('[iOS Debug] Request:', {
        url: config.url,
        method: config.method,
        withCredentials: config.withCredentials,
        hasToken: !!token,
        isIOS: isIOSSafari(),
        isWebView: isIOSWebView(),
        storageAvailable: isLocalStorageAvailable(),
      });
    }
    
    return config;
  },
  (error) => {
    console.error('[Auth] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Configure response interceptor with iOS-aware error handling
api.interceptors.response.use(
  (response) => {
    if (response.config.url?.includes('/auth/')) {
      console.log('[Auth] Response:', response.config.url, '→', response.status);
      
      // Log token receipt for debugging
      if (response.data?.token) {
        console.log('[Auth] Token received in response');
      }
    }
    return response;
  },
  (error) => {
    // Enhanced error logging for iOS debugging
    const url = error.config?.url || 'unknown';
    const isAuthRequest = url.includes('/auth/');
    
    if (isAuthRequest) {
      console.error('[Auth Error] Full details:', {
        url: url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        code: error.code,
        isIOS: isIOSSafari(),
        isWebView: isIOSWebView(),
      });
    }
    
    // Enhance error with more specific information
    if (error.response) {
      // Server responded with error status
      error.friendlyMessage = error.response.data?.detail || 
                              error.response.data?.message || 
                              `Server error (${error.response.status})`;
    } else if (error.request) {
      // Request was made but no response received
      if (error.code === 'ECONNABORTED') {
        error.friendlyMessage = 'Request timed out. Please check your connection and try again.';
      } else if (error.message?.includes('Network Error')) {
        // Detect potential CORS/cookie issues on iOS
        if (isIOSSafari() || isIOSWebView()) {
          error.friendlyMessage = 'Connection blocked. If using Private Browsing, please try normal mode.';
          error.isIOSIssue = true;
        } else {
          error.friendlyMessage = 'Network error. Please check your internet connection.';
        }
      } else {
        error.friendlyMessage = 'Unable to reach server. Please try again.';
      }
    } else {
      // Request setup error
      error.friendlyMessage = error.message || 'An unexpected error occurred.';
    }
    
    return Promise.reject(error);
  }
);

/**
 * iOS-safe login function with comprehensive error handling
 * IMPORTANT: Clears ALL previous session data before login
 */
export const performLogin = async (email, password, rememberMe = true) => {
  console.log('[Login] Starting iOS-safe login...');
  console.log('[Login] Environment:', {
    isIOS: isIOSSafari(),
    isWebView: isIOSWebView(),
    storageAvailable: isLocalStorageAvailable(),
  });
  
  // CRITICAL: Clear ALL previous session data BEFORE login
  // This prevents session leakage between users
  clearAuthToken();
  // Also clear any legacy token keys
  removeLocalItem('token');
  removeLocalItem('user');
  removeSessionItem('token');
  removeSessionItem('user');
  try { sessionStorage.clear(); } catch (_) { /* storage may be restricted in in-app browsers */ }
  console.log('[Login] Cleared all previous session data');
  
  try {
    const response = await api.post('/auth/login', {
      email,
      password,
      remember_me: rememberMe,
    });
    
    console.log('[Login] Response received:', {
      status: response.status,
      hasToken: !!response.data?.token,
      hasUser: !!response.data?.user,
    });
    
    // Store token using iOS-safe method
    if (response.data?.token) {
      setAuthToken(response.data.token, rememberMe);
      console.log('[Login] Token stored successfully');
    }
    
    return {
      success: true,
      user: response.data.user,
      token: response.data.token,
    };
  } catch (error) {
    console.error('[Login] Failed:', error);
    
    // Return structured error
    return {
      success: false,
      error: error.friendlyMessage || error.response?.data?.detail || 'Login failed',
      isIOSIssue: error.isIOSIssue || false,
      status: error.response?.status,
    };
  }
};

/**
 * iOS-safe signup function
 * IMPORTANT: Clears ALL previous session data before signup
 */
export const performSignup = async (userData, rememberMe = true) => {
  console.log('[Signup] Starting iOS-safe signup...');
  
  // CRITICAL: Clear ALL previous session data BEFORE signup
  // This prevents session leakage between users
  clearAuthToken();
  // Also clear any legacy token keys
  removeLocalItem('token');
  removeLocalItem('user');
  removeSessionItem('token');
  removeSessionItem('user');
  try { sessionStorage.clear(); } catch (_) { /* storage may be restricted in in-app browsers */ }
  console.log('[Signup] Cleared all previous session data');
  
  try {
    const response = await api.post('/auth/signup', userData);
    
    // Store token using iOS-safe method
    if (response.data?.token) {
      setAuthToken(response.data.token, rememberMe);
      console.log('[Signup] Token stored successfully');
    }
    
    return {
      success: true,
      user: response.data.user,
      token: response.data.token,
    };
  } catch (error) {
    console.error('[Signup] Failed:', error);
    
    return {
      success: false,
      error: error.friendlyMessage || error.response?.data?.detail || 'Signup failed',
      isIOSIssue: error.isIOSIssue || false,
      status: error.response?.status,
    };
  }
};

export default api;
